import { ref, provide, inject } from "vue";

// Symbols for provide/inject keys
const SIDEBAR_KEY = Symbol("sidebar");

// Default sidebar content
const DEFAULT_TITLE = "Matt(hew) Miller is a designer in Austin, TX";
const DEFAULT_DESCRIPTION =
  "Matt Miller is a designer in Austin, TX with over 17 years of experience working with agencies, startups, & individuals from all over the world.";

// Store route-based sidebar content
const routeSidebarContent = new Map();

// Composable for providing sidebar functionality (used in App.vue)
export function provideSidebar() {
  const title = ref(DEFAULT_TITLE);
  const description = ref(DEFAULT_DESCRIPTION);

  const updateSidebar = (newTitle, newDescription) => {
    title.value = newTitle || DEFAULT_TITLE;
    description.value = newDescription || DEFAULT_DESCRIPTION;
  };

  const resetSidebar = () => {
    title.value = DEFAULT_TITLE;
    description.value = DEFAULT_DESCRIPTION;
  };

  // Function to update sidebar content based on route name
  const updateSidebarForRoute = (routeName) => {
    const content = routeSidebarContent.get(routeName);
    if (content) {
      updateSidebar(content.title, content.description);
    } else {
      resetSidebar();
    }
  };

  provide(SIDEBAR_KEY, {
    title,
    description,
    updateSidebar,
    resetSidebar,
    updateSidebarForRoute,
  });

  return {
    title,
    description,
    updateSidebar,
    resetSidebar,
    updateSidebarForRoute,
  };
}

// Composable for consuming sidebar functionality (used in pages)
export function useSidebar() {
  const sidebar = inject(SIDEBAR_KEY);

  if (!sidebar) {
    throw new Error(
      "useSidebar must be used within a component that provides sidebar context"
    );
  }

  return sidebar;
}

// Function to register sidebar content for a route
export function registerSidebarContent(routeName, title, description) {
  routeSidebarContent.set(routeName, { title, description });
}

// Function to get sidebar content for a route (for router guards)
export function getSidebarContentForRoute(routeName) {
  return routeSidebarContent.get(routeName) || null;
}
